import type { InputHTMLAttributes, ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A checkbox in the brand palette. The native input stays in the tree - it
 * carries the focus, the keyboard behaviour and the accessibility tree - and
 * is made invisible with `peer` so the box beside it can be styled. Without
 * this, every checkbox in the app renders in the browser's own blue.
 */
export function Checkbox({
  label, hint, indeterminate, className, ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode;
  hint?: string;
  indeterminate?: boolean;
}) {
  return (
    <label
      className={cn(
        'group inline-flex items-start gap-2 text-sm text-ink',
        rest.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      <span className="relative flex size-[18px] shrink-0 items-center justify-center">
        <input
          {...rest}
          type="checkbox"
          className="peer absolute inset-0 size-full cursor-[inherit] appearance-none rounded-[5px] border border-line-strong bg-surface transition-colors checked:border-secondary checked:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
        />
        {indeterminate ? (
          <Minus className="pointer-events-none relative size-3 text-ink-subtle" aria-hidden />
        ) : (
          <Check
            className="pointer-events-none relative size-3 text-white opacity-0 transition-opacity peer-checked:opacity-100"
            aria-hidden
          />
        )}
      </span>

      {(label || hint) && (
        <span className="flex flex-col gap-0.5 leading-tight">
          {label && <span>{label}</span>}
          {hint && <span className="text-xs text-ink-subtle">{hint}</span>}
        </span>
      )}
    </label>
  );
}
